<script>
  import {createEventDispatcher, onMount, getContext, setContext, beforeUpdate} from 'svelte';
  import {stringify} from 'query-string';
  import {PATHS} from '@/const/paths';
  import {CONVERT_LANG_MAP} from '@/map';
  import {mc} from '@/core';
  import {drawerUtil} from '@/helper/drawer';
  import {popupUtil} from '@/helper/popup';
  import {navAfterRegisterSuccess} from '@/helper/login';
  import {doCaptcha} from '@/captcha';
  import {userApi} from '@/api/user';
  import {sd} from '@/da';
  import {writeLang} from '@/storage/lang';
  import {fixLink$} from '@/store/lang';
  import {activationStatus$} from '@/streams/auth';
  import Button from '@/widgets/Button.svelte';
  import EmailInput from '@/widgets/EmailInput.svelte';
  import PasswordInput from '@/widgets/PasswordInput.svelte';
  import DrawerModal from '@/widgets/DrawerModal.svelte';
  import {validateEmail} from '@/modules/components/register-form/validateUtil';
  import SubAccountWelcomeBackDialog from '@/modules/popups/welcome-back/SubAccountWelcomeBackDialog.svelte';
  import NewDeviceCodeForm from '@/modules/login/componets/NewDeviceCodeForm.svelte';
  import QrCode from '@/modules/login/componets/qr-code/QrCode.svelte';
  import MaskDialog from '@/modules/account/security/components/bind-passkeys/MaskDialog.svelte';
  import {referral$} from '@/store/referral';
  import PasskeyDesc from '@/modules/login/componets/passkey/PasskeyDesc.svelte';
  import {isEmpty, maskDialog$, passKeyClickEvent$} from '@/modules/login/componets/passkey/PasskeyLogin';
  import errors from '@/modules/error/error.lang';
  import lLang from '@/modules/login/LoginPage.lang';
  import lang from '../RegisterLandingPage.lang';
  import ThirdPartyLogin from '@/modules/login/componets/ThirdPartyLogin.svelte';
  import LoginTab from '@/modules/login/componets/LoginTab.svelte';
  import SplitLine from '@/modules/login/componets/SplitLine.svelte';
  import HeaderAuthentication from '@/modules/login/componets/HeaderAuthentication.svelte';

  export let LG = null;
  export let target = null;
  export let email = null;

  const dispatch = createEventDispatcher();
  const localLG = getContext('LG');

  let isLoading = false;
  let password = null;
  let errorEmailMsg = null;
  let dialogType = 'LOGIN';
  let loginInfo = createLoginInfo();
  let isOpenPasskey = true;
  let tryCount = 0;
  let maksDialog = 0;

  $: emailAccount = email && email.trim();
  $: errorEmailMsg = validateEmail(emailAccount, LG, lang);

  function initLG(LG, localLG) {
    if (LG && !localLG) {
      setContext('LG', LG);
    }
  }

  beforeUpdate(() => {
    initLG(LG, localLG);
  });
  onMount(() => sd.track('side_popup_open', {ui_position: 'login_popup'}));
  onMount(() => {
    maskDialog$.subscribe((v) => {
      if (v.visible) {
        maksDialog = popupUtil.create(MaskDialog);
      } else {
        maksDialog && maksDialog.$destroy();
      }
    });
  });

  function handleButtonNextClick() {
    passKeyClickEvent$.next({event: true});
  }

  function handleClickClose() {
    dispatch('close');
    resetData();
  }

  function handleEnterKey(e) {
    if (e.keyCode === 13) {
      if (isOpenPasskey) {
        handleButtonNextClick();
        return;
      }
      handleClickLogin();
    }
  }

  function handleStatusChange(p) {
    if (!isEmpty(p.isLoading)) {
      isLoading = p.isLoading;
    }
    if (!isEmpty(p.isOpenPasskey)) {
      isOpenPasskey = p.isOpenPasskey;
    }
  }

  async function handleClickLogin() {
    sd.track('launch_login', {ui_position: 'login_popup'});
    password = password || '';
    if (emailAccount && errorEmailMsg === null) {
      isLoading = true;
      const captchaResult = await doCaptcha('login');
      if (!captchaResult) {
        mc.send('toast', 'error', {message: LG(lang.NetworkError)});
        isLoading = false;
        return;
      }

      const params = {email: emailAccount, password};
      const {data, error, reply} = await userApi.login(captchaResult, params, LG);
      isLoading = false;
      if (data) {
        loginInfo = data;
        activationStatus$.next(data.status);
        if (data.newDevice === true) {
          dialogType = 'NEW_DEVICE_CODE_DIALOG';
          return;
        }
        dispatch('close');
        if (loginInfo.totp === 0) {
          drawerUtil.launchEmailVerification(LG, {
            type: 'login-verify',
            context: data,
            async callback(mailCode, callback, context) {
              const code = context.code;
              const data = await confirmLoginSure(code, mailCode, '');
              callback(data);
            },
          });
          return;
        }
        if (loginInfo.totp === 1) {
          drawerUtil.launchGoogleVerification(LG, {
            context: data,
            async callback(googleCode, callback, context) {
              const code = context.code;
              const data = await confirmLoginSure(code, '', googleCode);
              callback(data);
            },
          });
          return;
        }
      }
      if (error) {
        if (error.code === 80014) {
          popupUtil.create(SubAccountWelcomeBackDialog, {LG});
          return;
        }
        if (error.code === 34005) {
          mc.send('toast', 'error', {message: LG(errors[34005], {Times: reply ? reply.data.remainTimes : 0})});
          return;
        }
        mc.send('toast', 'error', error);
      }
    }
  }

  async function confirmLoginSure(code, mailCode, otpCode) {
    const query = {code};
    if (otpCode) {
      query.otpCode = otpCode;
    }
    if (mailCode) {
      query.mailCode = mailCode;
    }
    const {data, error} = await userApi.confirmLogin(query);
    if (data) {
      mc.send('toast', 'info', {message: LG(lang.LoggedIn)});
      const language = data.lang;
      language && writeLang(CONVERT_LANG_MAP[language] || language);
      localStorage.setItem('close-receive-member', 'N');
      localStorage.setItem('base-kyc-status', '1');
      setTimeout(() => {
        navAfterRegisterSuccess(checkTarget(target) && target);
      }, 200);
      return {code: 0, msg: ''};
    }
    if (error) {
      mc.send('toast', 'error', error);
      return error;
    }
  }

  function checkTarget(target) {
    if (!target) {
      return false;
    }

    if (target.indexOf('@') > -1) {
      return false;
    }

    const targetUrl = new URL(target);
    return targetUrl.host === location.host;
  }

  function resetData() {
    isLoading = false;
    email = null;
    password = null;
    errorEmailMsg = null;
  }

  function handleFocusFun(type) {
    return function () {
      sd.track('input_box_focus', {input_box_type: type, business_type: 'login', ui_position: 'login_popup'});
    };
  }

  function handleBlurFun(type) {
    return function () {
      sd.track('input_box_unfocus', {input_box_type: type, business_type: 'login', ui_position: 'login_popup'});
    };
  }

  function createLoginInfo() {
    return {code: '', totp: -1};
  }

  function handleNewDeviceCodeClick(otpCode, mailCode) {
    confirmLoginSure(loginInfo.code, mailCode, otpCode);
  }

  function handleGetStartedLogin() {
    drawerUtil.launchRegister(LG, target);
    handleClickClose();
  }
</script>

<style lang="scss">
  .close-btn:hover .iconfont {
    color: var(--T2);
  }
  .login-wrap {
    width: 100%;
    overflow: auto;
    .login-tab {
      padding: 0;
      width: 480px;
      color: #1d2029;
    }
  }
  .title {
    margin-top: 60px;
    line-height: 48px;
    font-weight: var(--fw3);
    font-size: 32px;
    color: #1d2029;
  }
  .sidebar-login-wrap {
    margin-top: 20px;
  }
  .login-row {
    margin-top: 20px;
  }
  .reset-password:hover {
    color: var(--blue);
  }
  .sidebar-login-wrap :global(button) {
    border-radius: 8px;
  }
  .form .error-msg {
    bottom: -16px;
    left: 0;
  }
  .c-grey {
    color: #6c6e74;
  }
  .login-wrap :global(.br48) {
    border-radius: 48px;
  }
  .login-wrap :global(button) {
    border-width: 0;
  }
  @media screen and (max-height: 650px) {
    .title {
      margin-top: 0;
    }
    .sidebar-login-wrap {
      margin-top: 20px;
    }
  }
</style>

<DrawerModal visible>
  <div class="login-wrap pr">
    <div class="close-btn tr cp" on:click={handleClickClose}><i class="iconfont f32 T4">&#xe614;</i></div>
    {#if dialogType === 'LOGIN'}
      <div class="df aic jcc wp100">
        <div class="login-tab">
          <div class="title df aic f32 lh48 fw3">{LG(lang.LogIn)}</div>
          <HeaderAuthentication class="mt24 br12" {LG} />
          <LoginTab {LG}>
            <div slot="email-login">
              <div class="form">
                <div class="f12 lh16 mt24 pl4 c-grey">{LG(lang.Email)}</div>
                <div class="mt4 pr">
                  <EmailInput
                    haveIcon={true}
                    class="mt4 br48 sidebar-email"
                    isError={errorEmailMsg}
                    placeholder={LG(lang.Email)}
                    bind:value={email}
                    disabled={isLoading}
                    on:keypress={handleEnterKey}
                    on:focus={handleFocusFun('email_input_box')}
                    on:blur={handleBlurFun('email_input_box')}
                  />
                  {#if errorEmailMsg}
                    <div class="sell f12 pa error-msg">{errorEmailMsg}</div>
                  {/if}
                </div>
                {#if !isOpenPasskey}
                  <div class="f12 lh16 mt20 pl4 c-grey">{LG(lang.Password)}</div>
                  <div class="mt4">
                    <PasswordInput
                      showIcon={false}
                      class="br48 sidebar-password"
                      placeholder={LG(lang.Password)}
                      bind:value={password}
                      disabled={isLoading}
                      on:keypress={handleEnterKey}
                      on:focus={handleFocusFun('password_input_box')}
                      on:blur={handleBlurFun('password_input_box')}
                    />
                  </div>
                {/if}
                {#if isOpenPasskey}
                  <PasskeyDesc {email} hasErrorMsg={errorEmailMsg !== null} {target} {LG} isInitializing={false} {isLoading} onStatusChange={handleStatusChange} />
                {:else}
                  <div class="sidebar-login-wrap">
                    <Button class="sidebar-login br48" minWidth="100%" type="primary" on:click={handleClickLogin} loading={isLoading}>{LG(lang.LogIn)}</Button>
                  </div>
                {/if}
                <div class="login-row df jcsb wp100 c-grey f12 lh16">
                  <div>
                    <span>{LG(lang.DontHaveAnAccount)}</span>
                    <a class="BLUE cp fw2" href="javascript:void(0);" on:click={handleGetStartedLogin}>{LG(lLang.SignUp)}</a>
                  </div>
                  {#if !isOpenPasskey}
                    <div><a class="reset-password cp f12 fw2" href={$fixLink$(PATHS.RESET_PASSWORD.pathname) + (target ? '?' + stringify({target}) : '')}>{LG(lLang.ForgotPassword)}</a></div>
                  {/if}
                </div>
                <SplitLine text={LG(lLang.OrContinueWith)} />
                <ThirdPartyLogin {target} entrance="Login" loginType="drawer-login" referralCode={$referral$.code} {LG} on:close={handleClickClose} />
              </div>
            </div>
            <div slot="qrcode-login">
              <QrCode target={checkTarget(target) ? target : $fixLink$(PATHS.HOME.pathname)} />
            </div>
          </LoginTab>
        </div>
      </div>
    {:else if dialogType === 'NEW_DEVICE_CODE_DIALOG'}
      <NewDeviceCodeForm isDrawer={true} onSubmit={handleNewDeviceCodeClick} {LG} isNeed2FA={loginInfo.totp === 1} {email} />
    {/if}
  </div>
</DrawerModal>
